import React from 'react';

const BAR = 'tw-rounded tw-bg-light-gray2 tw-animate-pulse';
const CIRCLE = 'tw-rounded-full tw-bg-light-gray2 tw-animate-pulse';
const SQUARE = 'tw-rounded-2xl tw-bg-light-gray2 tw-animate-pulse';

interface CountProps {
  count?: number;
}

export const ChatListItemsSkeleton: React.FC<CountProps> = ({ count = 7 }) => (
  <div className="tw-divide-y tw-divide-light-gray3 tw-w-full">
    {Array.from({ length: count }).map((_, i) => {
      const nameWidth = 40 + ((i * 13) % 35);
      const previewWidth = 55 + ((i * 17) % 35);
      return (
        <div
          key={i}
          className="tw-relative tw-grid tw-items-center tw-gap-3 tw-px-[calc(var(--unit-minthr)*22)] tw-py-2"
          style={{
            gridTemplateColumns: 'calc(var(--unit-minthr)*40) 1fr auto',
          }}
        >
          <div className={`${CIRCLE} tw-w-9 tw-h-9`} />
          <div className="tw-min-w-0">
            <div
              className={`${BAR} tw-h-[calc(var(--unit-minthr)*11)]`}
              style={{ width: `${nameWidth}%` }}
            />
            <div
              className={`${BAR} tw-mt-1.5 tw-h-[calc(var(--unit-minthr)*9)]`}
              style={{ width: `${previewWidth}%` }}
            />
          </div>
          <div className="tw-flex tw-flex-col tw-items-end tw-gap-1 tw-min-w-[calc(var(--unit-minthr)*40)]">
            <div
              className={`${BAR} tw-h-[calc(var(--unit-minthr)*9)] tw-w-[calc(var(--unit-minthr)*28)]`}
            />
          </div>
        </div>
      );
    })}
  </div>
);

export const ChatUserSelectSkeleton: React.FC<CountProps> = ({ count = 6 }) => (
  <div className="tw-w-full">
    {Array.from({ length: count }).map((_, i) => {
      const nameWidth = 45 + ((i * 11) % 25);
      const roleWidth = 25 + ((i * 13) % 20);
      return (
        <div
          key={i}
          className="tw-flex tw-items-center tw-gap-3 tw-px-1 tw-py-2"
        >
          <div className={`${CIRCLE} tw-w-9 tw-h-9 tw-shrink-0`} />
          <div className="tw-min-w-0 tw-flex-1">
            <div
              className={`${BAR} tw-h-[calc(var(--unit-minthr)*12)]`}
              style={{ width: `${nameWidth}%` }}
            />
            <div
              className={`${BAR} tw-mt-1.5 tw-h-[calc(var(--unit-minthr)*10)]`}
              style={{ width: `${roleWidth}%` }}
            />
          </div>
          <div className={`${CIRCLE} tw-w-8 tw-h-8 tw-shrink-0`} />
        </div>
      );
    })}
  </div>
);

interface MsgRow {
  side: 'in' | 'out';
  lines: string[];
  timeWidth: string;
}

const U = (n: number): string => `calc(var(--unit-minthr)*${n})`;

const MSG_PATTERN: MsgRow[] = [
  { side: 'in', lines: [U(120)], timeWidth: U(26) },
  { side: 'in', lines: [U(210), U(140)], timeWidth: U(26) },
  { side: 'out', lines: [U(160)], timeWidth: U(42) },
  { side: 'in', lines: [U(90)], timeWidth: U(26) },
  { side: 'out', lines: [U(230), U(120)], timeWidth: U(42) },
  { side: 'in', lines: [U(110)], timeWidth: U(26) },
];

const DatePill: React.FC = () => (
  <div className="tw-flex tw-justify-center tw-my-3">
    <div className="tw-h-[calc(var(--unit-minthr)*22)] tw-w-[calc(var(--unit-minthr)*64)] tw-rounded-full tw-bg-white tw-border tw-border-light-gray3 tw-shadow-sm tw-animate-pulse" />
  </div>
);

export const ChatMessagesSkeleton: React.FC = () => (
  <div className="chat-messages-container tw-flex-1 tw-min-h-0 tw-overflow-hidden tw-py-3 tw-flex tw-flex-col tw-gap-2">
    <DatePill />
    {MSG_PATTERN.map((row, i) => {
      const isOut = row.side === 'out';
      const radius = isOut
        ? 'tw-rounded-[calc(var(--unit-minthr)*14)] tw-rounded-br-[calc(var(--unit-minthr)*4)]'
        : 'tw-rounded-[calc(var(--unit-minthr)*14)] tw-rounded-bl-[calc(var(--unit-minthr)*4)]';
      const bubbleBg = isOut
        ? 'tw-bg-primary-opacity'
        : 'tw-bg-white tw-border tw-border-light-gray';
      const lineBg = isOut
        ? 'tw-bg-primary tw-opacity-30'
        : 'tw-bg-light-gray2';
      return (
        <React.Fragment key={i}>
          <div
            className={`tw-flex ${
              isOut ? 'tw-justify-end tw-pr-7' : 'tw-justify-start tw-pl-7'
            }`}
          >
            <div
              className={`${radius} ${bubbleBg} tw-shadow-sm tw-ps-3 tw-pe-4 tw-py-2  tw-flex tw-items-end tw-gap-2`}
            >
              <div className="tw-flex tw-flex-col tw-gap-1.5">
                {row.lines.map((w, li) => (
                  <div
                    key={li}
                    className={`tw-rounded ${lineBg} tw-animate-pulse tw-h-[calc(var(--unit-minthr)*11)]`}
                    style={{ width: w }}
                  />
                ))}
              </div>
              <div
                className={`tw-rounded ${lineBg} tw-animate-pulse tw-h-[calc(var(--unit-minthr)*8)] tw-shrink-0 tw-mb-0.5`}
                style={{ width: row.timeWidth }}
              />
            </div>
          </div>
          {i === 2 && <DatePill />}
        </React.Fragment>
      );
    })}
  </div>
);

export const GroupDetailsSkeleton: React.FC = () => (
  <>
    {/* Top bar (matches DetailsGroup back row) */}
    <div className="tw-flex tw-items-center tw-px-5 tw-py-3 tw-border-b tw-border-gray3 tw-bg-white">
      <div className={`${CIRCLE} tw-w-7 tw-h-7`} />
    </div>

    {/* Header / Avatar */}
    <div className="tw-text-center tw-px-6 tw-py-6 tw-border-b tw-border-gray3 tw-flex tw-flex-col tw-items-center">
      <div className={`${SQUARE} tw-mx-auto tw-w-24 tw-h-24 tw-rounded-3xl`} />
      <div
        className={`${BAR} tw-mt-4 tw-mb-2 tw-h-[calc(var(--unit-minthr)*22)] tw-w-[60%]`}
      />
      <div
        className={`${BAR} tw-mb-3 tw-h-[calc(var(--unit-minthr)*10)] tw-w-[50%]`}
      />
      <div className={`${BAR} tw-h-[calc(var(--unit-minthr)*12)] tw-w-[85%]`} />
      <div
        className={`${BAR} tw-mt-1.5 tw-h-[calc(var(--unit-minthr)*12)] tw-w-[70%]`}
      />
    </div>

    {/* Admins / Members */}
    <div className="tw-flex tw-gap-6 tw-px-6 tw-py-5 tw-border-b tw-border-gray3">
      {[0, 1].map((col) => (
        <div key={col} className="tw-flex-1">
          <div
            className={`${BAR} tw-h-[calc(var(--unit-minthr)*12)] tw-w-[calc(var(--unit-minthr)*70)] tw-mb-3`}
          />
          <div className="tw-flex tw-items-center">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={`${CIRCLE} tw-w-9 tw-h-9 tw-border-2 tw-border-white ${
                  i > 0 ? 'tw--ml-2' : ''
                }`}
              />
            ))}
          </div>
        </div>
      ))}
    </div>

    {/* Documents */}
    <div className="tw-px-6 tw-py-5">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
        <div
          className={`${BAR} tw-h-[calc(var(--unit-minthr)*12)] tw-w-[calc(var(--unit-minthr)*90)]`}
        />
        <div className="tw-flex tw-items-center tw-gap-2">
          <div className={`${CIRCLE} tw-w-8 tw-h-8`} />
          <div className={`${CIRCLE} tw-w-8 tw-h-8`} />
        </div>
      </div>
      <div className="tw-flex tw-gap-3 tw-overflow-hidden">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="tw-shrink-0 tw-w-[calc(var(--unit-minthr)*140)] tw-rounded-2xl tw-bg-light-gray2 tw-p-2"
          >
            <div
              className={`tw-w-full tw-h-[calc(var(--unit-minthr)*100)] tw-rounded-xl tw-bg-light-gray3 tw-animate-pulse`}
            />
            <div
              className={`${BAR} tw-mt-2 tw-h-[calc(var(--unit-minthr)*10)] tw-w-[70%]`}
            />
          </div>
        ))}
      </div>
    </div>
  </>
);

export const ConversationSkeleton: React.FC<{ isGroup?: boolean }> = ({
  isGroup = false,
}) => (
  <div className="tw-flex tw-flex-1 tw-flex-col tw-min-h-0 tw-bg-white">
    {/* Header Skeleton */}
    <div className="tw-shrink-0 tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-2.5 tw-py-1.5 tw-border-b tw-border-gray3 tw-bg-white">
      <div className="tw-flex tw-items-center tw-gap-3 tw-min-w-0 tw-flex-1">
        <div className={`${BAR} tw-w-5 tw-h-5 tw-shrink-0 tw-rounded-md`} />
        <div className={`${CIRCLE} tw-w-9 tw-h-9 tw-shrink-0`} />
        <div className="tw-min-w-0 tw-flex-1">
          <div
            className={`${BAR} tw-h-[calc(var(--unit-minthr)*13)] tw-w-[calc(var(--unit-minthr)*110)]`}
          />
          <div className="tw-mt-1.5 tw-flex tw-items-center tw-gap-1.5">
            <div className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-success tw-animate-pulse" />
            <div
              className={`${BAR} tw-h-[calc(var(--unit-minthr)*9)] tw-w-[calc(var(--unit-minthr)*55)]`}
            />
          </div>
        </div>
      </div>
      {isGroup && (
        <div className="tw-flex tw-items-center tw-gap-2 tw-shrink-0">
          <div className={`${CIRCLE} tw-w-7 tw-h-7`} />
          <div className={`${CIRCLE} tw-w-7 tw-h-7`} />
        </div>
      )}
    </div>

    {/* Messages Skeleton */}
    <ChatMessagesSkeleton />

    {/* Input Skeleton */}
    <div className="tw-shrink-0 tw-bg-white tw-px-3 tw-py-2.5">
      <div className="tw-flex tw-items-end tw-gap-2">
        <div className={`${BAR} tw-w-9 tw-h-9 tw-rounded-md tw-shrink-0`} />
        <div className="tw-flex-1 tw-flex tw-items-center tw-gap-2 tw-bg-light-gray tw-border tw-border-gray3 tw-rounded-[calc(var(--unit-minthr)*12)] tw-px-3 tw-h-9">
          <div
            className={`${BAR} tw-h-[calc(var(--unit-minthr)*11)] tw-flex-1`}
          />
          <div className={`${CIRCLE} tw-w-5 tw-h-5 tw-shrink-0`} />
        </div>
        <div className="tw-w-9 tw-h-9 tw-rounded-md tw-bg-primary-opacity tw-animate-pulse tw-shrink-0" />
      </div>
    </div>
  </div>
);
